import React, { useState } from "react";
import { HiChevronDown } from "react-icons/hi2";
import { WellnessLog } from "../../types/post";
import {
  useLogCardActions,
  LogCardActionsProps,
} from "../../hooks/useLogCardActions";
import LogCard from "../shared/LogCard";
import { LogCardChip } from "../shared/LogCardContent";

interface WellnessLogCardProps extends LogCardActionsProps<
  WellnessLog & { id: string }
> {
  log: WellnessLog & { id: string };
}

const WellnessLogCard: React.FC<WellnessLogCardProps> = ({
  log,
  onDelete,
  onEdit,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { handleDelete, handleEdit } = useLogCardActions(log, {
    onDelete,
    onEdit,
  });

  const handleCardClick = () => {
    setIsExpanded((prev) => !prev);
  };

  const getWellnessColor = (value: number): string => {
    const colorScale = [
      "#dc2626", // 1
      "#f87171",
      "#fca5a5",
      "#fde68a", // 4
      "#86efac",
      "#4ade80",
      "#22c55e", // 7
    ];
    return colorScale[value - 1] || "#22c55e";
  };

  const renderProgressBar = (
    label: string,
    value: number,
    maxValue: number = 7,
  ) => (
    <div className="mb-2">
      <div className="flex justify-between items-center mb-1">
        <span className="text-[11px] text-base-content/60 font-geist">
          {label}
        </span>
      </div>
      <div className="w-full bg-base-200 rounded-full h-1.5">
        <div
          className="h-1.5 rounded-full transition-all duration-300"
          style={{
            width: `${(value / maxValue) * 100}%`,
            backgroundColor: getWellnessColor(value),
          }}
        />
      </div>
    </div>
  );

  return (
    <div onClick={handleCardClick} className="cursor-pointer">
      <LogCard
        className={isExpanded ? "bg-base-100" : "bg-green-500/10"}
        onDelete={onDelete ? handleDelete : undefined}
        onEdit={onEdit ? handleEdit : undefined}
      >
        {/* Header */}
        <div className="flex items-center gap-2 pr-8">
          <span className="text-[11px] font-medium tracking-wide uppercase text-base-content/70 font-geist">
            Wellness
          </span>
          <span className="text-[11px] font-bold text-green-800 dark:text-green-400 bg-green-500/15 px-1.5 py-0.5 rounded font-geist">
            {log.wellnessScore}
          </span>
          <HiChevronDown
            className={`w-3 h-3 text-base-content/30 transition-transform duration-200 ml-auto ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </div>

        {isExpanded && (
          <div className="mt-3 bg-base-200/60 rounded-lg p-3 space-y-0.5">
            {renderProgressBar("Health", log.health)}
            {renderProgressBar("Mood", log.mood)}
            {renderProgressBar("Physical", log.physical)}
            {renderProgressBar("Recovery", log.recovery)}
            {renderProgressBar("Sleep", log.sleep)}
            {renderProgressBar("Stress", log.stress)}

            {log.sick && (
              <div className="pt-1">
                <LogCardChip label="Sick" variant="warning" />
              </div>
            )}
          </div>
        )}
      </LogCard>
    </div>
  );
};

export default WellnessLogCard;
